import React, { useEffect } from 'react';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import ScheduleBuilderTab from './ScheduleBuilderTab';

/**
 * Standalone entry point for the schedule-format wizard — reachable from the
 * Tournaments list ("Explore Formats" button) rather than from inside a real
 * tournament's Schedule tab. Built for demos: a bigger academy that already
 * runs tournaments daily can try any format on realistic mock data before
 * ever creating one, then apply what they liked to a real tournament later.
 * Renders the exact same wizard (`ScheduleBuilderTab`) a real tournament's
 * Schedule tab does, just in `standalone` mode — see useScheduleBuilder.js
 * for how that mode adds the upfront Test Data step and skips seeding from
 * real tournament data (there isn't any here).
 */
const ExploreTournamentFormatsPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { setBreadcrumbItems } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Home', path: '/' },
      { label: 'Tournaments', path: '/tournaments' },
      { label: 'Explore Formats' },
    ]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-7xl mx-auto px-4 py-6 lg:px-0">
        <div className="flex items-start gap-3 mb-8">
          <button
            onClick={() => navigate('/tournaments')}
            className={`mt-1 p-2 ${theme.cardHover} rounded-lg transition-colors flex-shrink-0`}
            title="Back to Tournaments"
          >
            <ArrowLeft className={`w-5 h-5 ${theme.textSecondary}`} />
          </button>
          <div>
            <h1 className={`flex items-center gap-2 text-3xl font-bold ${theme.text} mb-2`}>
              <Sparkles className="w-7 h-7" />
              Explore Tournament Formats
            </h1>
            <p className={theme.textSecondary}>
              Try any schedule format on realistic mock data — no tournament required.
            </p>
          </div>
        </div>

        <ScheduleBuilderTab standalone />
      </div>
    </div>
  );
};

export default ExploreTournamentFormatsPage;
